<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="dlm-backdrop" @click.self="$emit('close')">
        <div class="dlm-panel">

          <!-- Header -->
          <div class="dlm-header">
            <div>
              <p class="dlm-step-label">Download</p>
              <h2 class="dlm-title">Choose what to download</h2>
            </div>
            <button @click="$emit('close')" class="dlm-close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="dlm-body">
            <div class="grid grid-cols-2 gap-3">

              <!-- Option 1: CV only -->
              <button type="button" class="dlm-card" @click="chooseCvOnly">
                <svg class="w-5 h-5 text-zinc-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="text-sm font-semibold text-zinc-900 dark:text-white mb-1">CV only</p>
                <p class="text-xs text-zinc-400 leading-relaxed">Your CV as a single PDF document</p>
              </button>

              <!-- Option 2: CV + references (Pro) -->
              <button
                v-if="isPro"
                type="button"
                class="dlm-card"
                :disabled="!hasReferenceDocuments"
                @click="chooseWithReferences"
              >
                <span class="flex items-center justify-between w-full mb-3">
                  <svg class="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span class="dlm-pro-pill">Pro</span>
                </span>
                <p class="text-sm font-semibold text-zinc-900 dark:text-white mb-1">CV + references</p>
                <p class="text-xs text-zinc-400 leading-relaxed">{{ referencesSubtitle }}</p>
              </button>

              <!-- Option 2 for free users: grayed out with upgrade CTA -->
              <div v-else class="dlm-card dlm-card--locked">
                <span class="flex items-center justify-between w-full mb-3">
                  <svg class="w-5 h-5 text-zinc-300 dark:text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span class="dlm-pro-pill">Pro</span>
                </span>
                <p class="text-sm font-semibold text-zinc-400 dark:text-zinc-500 mb-1">CV + references</p>
                <p class="text-xs text-zinc-400 leading-relaxed">Merge certificates, letters and reviews with your CV into one PDF</p>
                <button type="button" class="dlm-upgrade-btn" :disabled="isRedirecting" @click="handleUpgrade">
                  {{ isRedirecting ? 'Redirecting…' : 'Upgrade to Pro' }}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useSubscription } from '../composables/useSubscription.js'

export default {
  name: 'CvDownloadOptionsModal',
  props: {
    isOpen: { type: Boolean, required: true },
    cvId: { type: [String, Number], default: null }
  },
  emits: ['close', 'download-cv-only', 'download-cv-with-references'],
  setup(props, { emit }) {
    const { isPro, openCheckout } = useSubscription()

    const isRedirecting = ref(false)
    // null = not loaded yet (treat the option as available; the server is authoritative)
    const referencesCount = ref(null)

    const hasReferenceDocuments = computed(() =>
      referencesCount.value === null || referencesCount.value > 0
    )

    const referencesSubtitle = computed(() => {
      if (referencesCount.value === null) {
        return 'Appends all attached reference documents into one merged PDF'
      }
      if (referencesCount.value === 0) {
        return 'No reference documents attached to this CV'
      }
      const n = referencesCount.value
      return `Appends ${n} reference document${n !== 1 ? 's' : ''} into one merged PDF`
    })

    const fetchReferencesInfo = async () => {
      referencesCount.value = null
      if (!props.cvId) {
        referencesCount.value = 0
        return
      }
      try {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        const res = await fetch(`/api/cvs/${props.cvId}/references/info`, {
          headers: { Accept: 'application/json', 'X-CSRF-Token': csrf }
        })
        const data = await res.json()
        if (data.success) referencesCount.value = data.total_references || 0
      } catch {
        // Leave count unknown — the option stays enabled and the server decides
      }
    }

    watch(() => props.isOpen, (open) => { if (open) fetchReferencesInfo() })

    const chooseCvOnly = () => {
      emit('download-cv-only')
      emit('close')
    }

    const chooseWithReferences = () => {
      if (!hasReferenceDocuments.value) return
      emit('download-cv-with-references')
      emit('close')
    }

    const handleUpgrade = async () => {
      isRedirecting.value = true
      try {
        await openCheckout()
      } finally {
        isRedirecting.value = false
      }
    }

    return {
      isPro,
      isRedirecting,
      hasReferenceDocuments,
      referencesSubtitle,
      chooseCvOnly,
      chooseWithReferences,
      handleUpgrade
    }
  }
}
</script>

<style scoped>
.dlm-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}
.dlm-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-xl flex flex-col overflow-hidden;
}
.dlm-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.dlm-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.dlm-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.dlm-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.dlm-body {
  @apply px-6 py-5;
}
.dlm-card {
  @apply flex flex-col items-start text-left p-5 border border-zinc-100 dark:border-zinc-800 rounded-xl hover:border-primary dark:hover:border-primary-400 transition-colors cursor-pointer;
}
.dlm-card:disabled {
  @apply opacity-50 cursor-not-allowed hover:border-zinc-100 dark:hover:border-zinc-800;
}
.dlm-card--locked {
  @apply cursor-default hover:border-zinc-100 dark:hover:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30;
}
.dlm-pro-pill {
  @apply inline-flex items-center border border-gold dark:border-gold-500 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gold dark:text-gold-400 px-2 py-0.5 leading-tight flex-shrink-0;
}
.dlm-upgrade-btn {
  @apply mt-3 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
